import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as xlsx from '@jobindsats/XLSX';
import * as excelJs from '@jobindsats/exceljs';

@customElement('maaling-export-element')
export class MaalingExportElement extends LitElement {

    static styles = css`
        .container {
            margin-bottom: 10px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            max-width: 210px;
            padding-right: 100px;
        }
        .button-container {
            display: flex;
            gap: 10px;
            margin-top: 15px;
        }
        .export-button {
            border: solid;
            border-width: 2px;
            border-color: var(--main-blue);
            background: transparent;
            padding: 12px 30px;
            color: var(--main-blue);
            cursor: pointer;
        }
        .export-button:hover {
            background-color: var(--main-blue-10);
        }
        .button-box {
            display: flex;
            flex-direction: column;
        }
        .merge-cbox {
            margin-top: 10px;
        }
        input[type=checkbox] {
            margin-left: 0px;
        }
        .text {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
        }

    `;

    @property({ type: Element })
    table: Element | undefined;

    @property({ type: String })
    header: String | undefined;

    @property({ type: String })
    source: String | undefined;

    @property({ type: String })
    footer: String | undefined;

    @property()
    exportJsonData: String | undefined;

    @property({ type: String })
    maalingId: String | undefined;

    @property({ type: String })
    themeTitle: String | undefined;

    @property({ type: String })
    exportTitle: String | undefined;

    @state()
    mergeChecked?: boolean = true;

    render() {
        return html`
            <section class="container">
                <div class="text">Eksport af data</div>
                <div>Vælg hvilket format du ønsker dit data eksporteret som.</div>
                <div class="button-container">
                    <div class="button-box">
                        <button class="export-button" id="exportbuttonExcel" @click=${this.handleExcelButtonClick}>
                            <div class="glyphicon glyphicon-export"></div>
                            <div>Excel</div>
                        </button>
                            <div class="merge-cbox">
                                <input class="name-input" type="checkbox" id="mergeCheckbox" name="cbox" value="mergeCheck" .checked="${this.mergeChecked}" @click=${() => this.handleMergeClicked()}>
                                <label for="mergeCheckbox">Flet celler</label><br>
                            </div>
                    </div>
                    <div class="button-box">
                        <button class="export-button" id="exportbuttonJSON" @click=${this.handleJsonButtonClick}>
                            <div class="glyphicon glyphicon-export"></div>
                            <div>JSON</div>
                        </button>
                    </div>
                </div>
            </section>
        `;
    }

    handleJsonButtonClick() {
        const element = document.createElement('a');
        element.setAttribute('href', `data:text/json;charset=UTF-8,${encodeURIComponent(this.exportJsonData as string)}`);
        element.setAttribute('download', this.header ? `export_${this.header}.json` : 'export.json');
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }

    handleMergeClicked() {
        this.mergeChecked = !this.mergeChecked;
    }

    getHighestRowNumber(sheet: xlsx.WorkSheet) {

        const keys = Object.keys(sheet);
        let highestRow = 0;
        keys.forEach((key) => {
            const rowNumber = parseInt(key.replace(/\D/g, ''));
            if (rowNumber > highestRow) highestRow = rowNumber;
        })
        return highestRow;
    };

    fillBlankSpacesInSheet(sheet: xlsx.WorkSheet, columns: string[], highestRow: number) {
        for (let n = 0; n < columns.length; n++) {
                for (let i = 1; i <= highestRow; i++) {
                    const cell = columns[n]+i;
                    if(!sheet[cell]) {
                        const previousCell = columns[n]+(i-1);
                        sheet[cell] = sheet[previousCell];
                    }
                }
            }
        return sheet;
    }

    handleExcelButtonClick() {
        const databanktable = this.table;
        const headerText = this.header;
        const sourcesText = this.source;
        const footerText = this.footer;
        const excelTitle = this.exportTitle;

        let sheet = xlsx.utils.table_to_sheet(databanktable, { cellStyles: true, raw: true });
        // If merge is not checked, we must make sure there's no empty cell in geography column (column A)
        if (!this.mergeChecked) {
            // Returns highest row number from sheet, so we know how many times to iterate when filling blank spaces
            const highestRowInSheet = this.getHighestRowNumber(sheet);
            // Modifying sheet so we do not have any blank spaces in column A
            let columnsToFill = ["A","B"];   
            sheet = this.fillBlankSpacesInSheet(sheet, columnsToFill, highestRowInSheet);           
        }


        const workbook = new excelJs.Workbook();
        var ws = workbook.addWorksheet('Ark 1');
        // Add values to cells
        for (let key in sheet) {
            if (Object.prototype.hasOwnProperty.call(sheet, key)) {
                const value = sheet[key];
                for (let k in value) {
                    if (Object.prototype.hasOwnProperty.call(value, k)) {
                        if (k === 'v') {
                            try {
                                if (value[k] !== "") {
                                    const cell = ws.getCell(key.toString());

                                    if (value[k] !== ".") {
                                        const cellNumericValue =
                                            Number(value[k].toString().replaceAll(".", "").replaceAll(',', '.'));
                                        
                                        if (isNaN(cellNumericValue) || this.isYear(value[k].toString())) {
                                            cell.value = value[k];
                                        } else {
                                            cell.value = cellNumericValue;

                                            if (Number.isInteger(cellNumericValue)) {
                                                cell.style.numFmt = '#,##0';
                                            } else {
                                                var decimals = cellNumericValue.toString().split(".")[1].length;
                                                cell.style.numFmt = '#,##0.' + "0".repeat(decimals);
                                            }

                                            cell.alignment = {
                                                vertical: 'top',
                                                horizontal: 'right'
                                            };
                                        }
                                    }
                                    else {
                                        cell.value = value[k];
                                    }

                                    cell.border = {
                                        bottom: { style: 'hair', color: { argb: 'FF003087' } },
                                        top: { style: 'hair', color: { argb: 'FF003087' } },
                                        left: { style: 'hair', color: { argb: 'FF003087' } },
                                        right: { style: 'hair', color: { argb: 'FF003087' } },
                                    };
                                    if (cell.type === 3 && cell.value !== '.') {

                                        cell.alignment = {
                                            vertical: 'top',
                                            horizontal: 'center'
                                        };

                                        cell.fill = {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: 'FFE5EAF3' }
                                        };
                                    }
                                    if (cell.value === '.') {
                                        cell.alignment = {
                                            horizontal: 'right'
                                        };
                                    }
                                    const column = ws.getColumn(key.replace(/[0-9]/g, ''));
                                    const cellWidth = cell.value?.toString().length;
                                    if (column.width && cellWidth) {
                                        if (column.width < cellWidth) {
                                            column.width = cellWidth + 5;
                                        }
                                    } else {
                                        if (cellWidth) {
                                            column.width = cellWidth + 5;
                                        }
                                    }
                                }
                            } catch (e) {
                                console.error('Invalid key encountered: ${key}');
                            }
                        }
                    }
                }
            }
        }
        const headerRow = ws.insertRow(1, [excelTitle]);
        // Merges
        for (let key in sheet) {
            if (Object.prototype.hasOwnProperty.call(sheet, key)) {
                if (key === '!merges') {
                    const merges = sheet[key];
                    if (merges?.length)
                        for (let m = 0; m < merges.length; m++) {
                            var startRow = 0;
                            var endRow = 0;
                            var startCol = 0;
                            var endCol = 0;
                            const merge: any = merges[m];
                            for (let n in merge) {
                                if (Object.prototype.hasOwnProperty.call(merge, n)) {
                                    if (n === 's') {
                                        const start = merge[n];
                                        for (let rowcol in start) {
                                            if (Object.prototype.hasOwnProperty.call(start, rowcol)) {
                                                if (rowcol === 'c') {
                                                    startCol = parseInt(start[rowcol].toString()) + 1;
                                                }
                                                if (rowcol === 'r') {
                                                    startRow = parseInt(start[rowcol].toString()) + 2;
                                                }
                                            }
                                        }
                                    }
                                    if (n === 'e') {
                                        const end = merge[n];
                                        for (let rowcol in end) {
                                            if (Object.prototype.hasOwnProperty.call(end, rowcol)) {
                                                if (rowcol === 'c') {
                                                    endCol = parseInt(end[rowcol].toString()) + 1;
                                                }
                                                if (rowcol === 'r') {
                                                    endRow = parseInt(end[rowcol].toString()) + 2;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            if (this.mergeChecked) {
                                ws.mergeCells(startRow, startCol, endRow, endCol);
                            }
                        }
                }
            }
        }

        const spacingBottomRow = ws.addRow("");
        ws.addRow("");

        const sourcesRow = ws.addRow([sourcesText]);
        const footerRow = ws.addRow([footerText]);

        headerRow.getCell(1).font = {
            size: 18,
            bold: true
        };
        for (let i = 1; i < ws.actualColumnCount + 1; i++) {
            headerRow.getCell(i).border = {
                bottom: { style: 'thick', color: { argb: 'FF003087' } },
            };
            spacingBottomRow.getCell(i).border = {
                top: { style: 'thick', color: { argb: 'FF003087' } },
            };
        }
        // Saving the file
        var dateString = new Date().toLocaleDateString('da-DK').split('/').join('.');
        workbook.xlsx.writeBuffer()
            .then(xls64 => {
                var a = document.createElement('a');
                var data = new Blob([xls64], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                var url = URL.createObjectURL(data);
                a.href = url;
                a.download = this.maalingId ? `DB_${this.themeTitle}_${this.maalingId}_${dateString}.xlsx` : 'export.xlsx';
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                },
                    0);
            })
            .catch(error => {
                console.error(error.message);
            });
    }

    private isYear(year: string) {
        return year.length === 4 && !year.includes('.') && !year.includes(',') && !year.includes('-');
    }

}